
import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { fetchMetrics, checkUserInteractions } from '@/services/socialMetricsService';
import { SocialMetrics } from '@/types/social';
import { ContentType } from '@/services/comments/types';
import { useAnonymousId } from '@/hooks/useAnonymousId';

/**
 * Hook for loading and managing social metrics for content
 */
export const useMetricsLoader = (
  contentId: string, 
  contentType: ContentType = 'card',
  externalCommentCount?: number
) => {
  const [metrics, setMetrics] = useState<SocialMetrics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user, isAuthenticated } = useAuth();
  const { anonymousId } = useAnonymousId();
  
  /**
   * Load metrics from the server
   */
  const loadMetrics = useCallback(async () => {
    if (!contentId) return;
    
    try {
      setIsLoading(true);
      
      // Fetch metrics from the server
      const socialMetrics = await fetchMetrics(contentId, contentType);
      
      // Check if the user has liked the content
      const interactions = await checkUserInteractions(
        contentId, 
        isAuthenticated ? user?.id || null : null,
        !isAuthenticated ? anonymousId : null,
        contentType
      );
      
      // Update metrics with external comment count if provided
      const updatedMetrics = {
        ...socialMetrics,
        ...interactions,
        commentsCount: externalCommentCount !== undefined ? externalCommentCount : socialMetrics.commentsCount
      };
      
      setMetrics(updatedMetrics);
    } catch (error) {
      console.error('Error loading social metrics:', error);
    } finally {
      setIsLoading(false);
    }
  }, [contentId, contentType, user?.id, isAuthenticated, anonymousId, externalCommentCount]);
  
  /**
   * Update metrics with a new comment count
   */
  const updateWithNewCommentCount = useCallback((count: number) => {
    if (metrics) {
      setMetrics(prev => prev ? { ...prev, commentsCount: count } : null);
    }
  }, [metrics]);
  
  return { 
    metrics,
    isLoading, 
    loadMetrics,
    updateWithNewCommentCount
  };
};
